// @ts-strict-ignore
import React from 'react';
import _ from 'lodash';
import { Field, useForm } from 'react-final-form';
import { Checkbox } from '@/core/Checkbox.atom';
import { useTranslation } from 'react-i18next';
import { ValidatingFormComponent } from '@/formbuilder/formBuilder.constants';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { Y_VALUE_BIN_MODES } from '@/tools/histogram/histogram.constants';
import { getFormFieldProps } from '@/formbuilder/formbuilder.utilities';
import { TextField } from '@seeqdev/qomponents';

export type ValueBins = {
  binSize: number;
  numberOfBins: number;
  min: number;
  max: number;
};

export interface ValueBinsIF extends ValidatingFormComponent<ValueBins> {
  component: 'ValueBinsFormComponent';
  yValueBinMode: string;
  setBinCreationMode: (mode: string) => void;
  rowClasses: string;
  onBinSizeChange: (binSize: number) => void;
  onBinNumberChange: (binNumber: number) => void;
  onMaxChange: (max: number) => void;
  onMinChange: (min: number) => void;
}

const isNotValid = (value: number) => _.isNil(value) || _.isNaN(value);

export const ValueBinsFormComponent: React.FunctionComponent<ValueBinsIF> = (props) => {
  const {
    testId = 'valueBins',
    label,
    name,
    yValueBinMode,
    setBinCreationMode,
    rowClasses,
    value,
    onBinNumberChange,
    onBinSizeChange,
    onMaxChange,
    onMinChange,
  } = props;

  const { t } = useTranslation();
  const formState = useForm().getState();

  const isBinSizeInValid = (binSize: number) => !isNotValid(binSize) && binSize <= 0;

  const isNumberOfBinsInValid = (numberOfBins: number) => !isNotValid(numberOfBins) && numberOfBins <= 0;

  const isBinSizeAndNumberInValid = (value: ValueBins) => isNotValid(value?.binSize) && isNotValid(value?.numberOfBins);

  const invalidMinMax = value?.min >= value?.max;

  const showError =
    (_.has(formState.errors, name) && _.get(formState.modified, name)) || formState.submitFailed || invalidMinMax;

  const defaultValidation = (value: ValueBins) =>
    isBinSizeAndNumberInValid(value) ||
    isBinSizeInValid(value?.binSize) ||
    isNumberOfBinsInValid(value?.numberOfBins) ||
    isNotValid(value?.min) ||
    isNotValid(value?.max) ||
    invalidMinMax;

  const getCustomErrorText = ({ binSize, numberOfBins, min, max }: ValueBins) => {
    if (isBinSizeInValid(binSize)) {
      return 'AGGREGATIONS.INVALID_BIN_SIZE';
    }
    if (isNumberOfBinsInValid(numberOfBins)) {
      return 'AGGREGATIONS.INVALID_BIN_NUMBER';
    }
    if (min >= max) {
      return 'AGGREGATIONS.MIN_EXCEEDS_MAX';
    }
  };

  const includeZero = (value: number) => (_.isUndefined(value) ? '' : value);

  const getValue = (value: string) => (value === '' ? undefined : Number(value));

  return (
    <FormFieldWrapper
      testId={testId}
      label={label}
      showError={showError}
      customErrorText={getCustomErrorText(value)}
      wrapperClassNames="width-100percent">
      <Field name={name} validate={defaultValidation}>
        {({ input, meta }) => {
          const { onChange, value } = getFormFieldProps(formState, input, meta, props);
          return (
            <>
              <table className="table table-condensed mb0">
                <tbody>
                  <tr className={rowClasses}>
                    <td>
                      <Checkbox
                        id="size"
                        label="AGGREGATIONS.SIZE_OF_BIN"
                        isChecked={yValueBinMode === Y_VALUE_BIN_MODES.SIZE}
                        type="radio"
                        onChange={() => setBinCreationMode(Y_VALUE_BIN_MODES.SIZE)}
                      />
                    </td>
                    <td>
                      <TextField
                        autoComplete="off"
                        testId="binSize-input"
                        type="number"
                        size="sm"
                        extraClassNames="width-75"
                        value={includeZero(value.binSize)}
                        onChange={(e) => {
                          const binSize = getValue(e.target.value);
                          onChange({ ...value, binSize });
                          onBinSizeChange(binSize);
                          setBinCreationMode(Y_VALUE_BIN_MODES.SIZE);
                        }}
                        showError={showError && (isBinSizeInValid(value.binSize) || isBinSizeAndNumberInValid(value))}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Checkbox
                        id="number"
                        label="AGGREGATIONS.NUMBER_OF_BINS"
                        isChecked={yValueBinMode === Y_VALUE_BIN_MODES.NUMBER}
                        type="radio"
                        onChange={() => setBinCreationMode(Y_VALUE_BIN_MODES.NUMBER)}
                      />
                    </td>
                    <td>
                      <TextField
                        autoComplete="off"
                        testId="numberOfBins-input"
                        type="number"
                        size="sm"
                        extraClassNames="width-75"
                        value={includeZero(value.numberOfBins)}
                        onChange={(e) => {
                          const numberOfBins = getValue(e.target.value);
                          onChange({
                            ...value,
                            numberOfBins,
                          });
                          onBinNumberChange(numberOfBins);
                          setBinCreationMode(Y_VALUE_BIN_MODES.NUMBER);
                        }}
                        showError={
                          showError && (isNumberOfBinsInValid(value.numberOfBins) || isBinSizeAndNumberInValid(value))
                        }
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="flexFill flexColumnContainer flexAlignCenter mt5" data-testid={`${testId}MinMax`}>
                <span className="pr10">{t('AGGREGATIONS.MIN')}</span>
                <TextField
                  testId={`${testId}Min`}
                  autoComplete="off"
                  type="number"
                  size="sm"
                  extraClassNames="width-75"
                  value={includeZero(value.min)}
                  onChange={(e) => {
                    const min = getValue(e.target.value);
                    onChange({ ...value, min });
                    onMinChange(min);
                  }}
                  showError={showError && (isNotValid(value.min) || value.min > value.max)}
                />
                <span className="pl10 pr10">{t('AGGREGATIONS.MAX')}</span>
                <TextField
                  testId={`${testId}Max`}
                  autoComplete="off"
                  type="number"
                  size="sm"
                  extraClassNames="width-75"
                  value={includeZero(value.max)}
                  onChange={(e) => {
                    const max = getValue(e.target.value);
                    onChange({ ...value, max });
                    onMaxChange(max);
                  }}
                  showError={showError && (isNotValid(value.max) || value.min > value.max)}
                />
              </div>
            </>
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
